import React, { useState } from 'react';
import { CheckCircle, User } from 'lucide-react';
import { Button } from '@/components/ui';
import { Input, Field, FieldLabel, FieldError, PhoneField } from '../../../components/ui';
import type { Region } from '@court-sync/shared';
import type { Student } from '@court-sync/shared';
import { isStudentProfileCompleteForBooking } from '@court-sync/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/Dialog';
import type { StudentFormData } from './schemas';
import type { StudentFormReturn } from './types';
import type { AuthenticatedUser } from './types';
import { cn } from '../../../utils/cn';

export interface PublicBookingStudentFormStepProps {
  form: StudentFormReturn;
  onSubmit: (data: StudentFormData) => void;
  isSubmitting: boolean;
  authenticatedUser: AuthenticatedUser | null;
  authStoreUser: AuthenticatedUser | null;
  hasPaymentProcessing: boolean;
  /** Existing children for logged-in guardians. When selected, skips form after confirmation. */
  existingStudents?: Student[];
  /** Self-student record for the logged-in guardian, when one exists for this academy. */
  selfStudent?: Student | null;
  /** Called when user confirms an existing child. Parent should create booking with studentId and advance. */
  onSelectExistingStudent?: (student: Student) => void;
  /** Called when logged-in user confirms booking for themselves. Skips form. */
  onSelectMyself?: () => void;
  /** When set, hides the "Who are you booking for?" picker; form is fixed to child vs myself. */
  lockBookingFor?: 'child' | 'myself';
  /** Alters headings and submit label (e.g. waitlist vs booking checkout). */
  variant?: 'booking' | 'waitlist';
  /** Optional back control when variant is waitlist (return to student selection). */
  onBackFromWaitlistForm?: () => void;
  defaultCountrySource?: { country?: string | null; region?: Region | null };
}

export function PublicBookingStudentFormStep({
  form,
  onSubmit,
  isSubmitting,
  authenticatedUser,
  authStoreUser,
  hasPaymentProcessing,
  existingStudents = [],
  selfStudent = null,
  onSelectExistingStudent,
  onSelectMyself,
  lockBookingFor,
  variant = 'booking',
  onBackFromWaitlistForm,
  defaultCountrySource,
}: PublicBookingStudentFormStepProps) {
  const bookingFor = form.watch('bookingFor');
  const useCustomerAsEmergencyContact = form.watch('useCustomerAsEmergencyContact');
  const smsOptIn = form.watch('smsOptIn');
  const useMergedGuardianContact =
    bookingFor === 'child' && useCustomerAsEmergencyContact !== false;
  const [confirmStudent, setConfirmStudent] = useState<Student | null>(null);
  const [confirmMyself, setConfirmMyself] = useState(false);

  React.useEffect(() => {
    if (lockBookingFor) {
      form.setValue('bookingFor', lockBookingFor);
    }
  }, [lockBookingFor, form]);

  const hasExistingChildren =
    !lockBookingFor && existingStudents.length > 0 && onSelectExistingStudent;

  const _isLoggedIn = !!(authenticatedUser || authStoreUser);
  const canSkipMyself =
    (!lockBookingFor || lockBookingFor === 'myself') &&
    !!selfStudent &&
    isStudentProfileCompleteForBooking(selfStudent) &&
    !!onSelectMyself;

  const showStudentFields =
    (bookingFor === 'child' || bookingFor === 'myself') &&
    !(canSkipMyself && lockBookingFor === 'myself');

  const user = authenticatedUser || authStoreUser;

  // Pre-populate contact info and emergency contact when logged in (only if not already filled)
  React.useEffect(() => {
    if (!user) return;
    const currentFirst = form.getValues('customerFirstName');
    if (currentFirst?.trim()) return; // Already filled, don't overwrite

    const firstName = user.firstName ?? '';
    const lastName = user.lastName ?? '';
    const email = user.email ?? '';
    const phone = (user as { phone?: string }).phone ?? '';

    if (firstName) form.setValue('customerFirstName', firstName);
    if (lastName) form.setValue('customerLastName', lastName);
    if (email) form.setValue('customerEmail', email);
    if (phone) form.setValue('customerPhone', phone);

    if (form.getValues('bookingFor') === 'child') {
      form.setValue('useCustomerAsEmergencyContact', true);
    }
  }, [user, form]);

  // Auto-fill customer/student name from user when booking for self (logged-in users only)
  React.useEffect(() => {
    if (bookingFor === 'myself' && user) {
      const userFirst = user.firstName ?? '';
      const userLast = user.lastName ?? '';
      if (userFirst) form.setValue('customerFirstName', userFirst);
      if (userLast) form.setValue('customerLastName', userLast);
    }
  }, [bookingFor, form, user]);

  // Sync firstName/lastName with customer fields when booking for myself (pre-fill and keep in sync)
  const customerFirst = form.watch('customerFirstName');
  const customerLast = form.watch('customerLastName');
  React.useEffect(() => {
    if (bookingFor === 'myself') {
      form.setValue('firstName', customerFirst ?? '');
      form.setValue('lastName', customerLast ?? '');
    }
  }, [bookingFor, customerFirst, customerLast, form]);

  // Clear student information when switching to "My Child"
  React.useEffect(() => {
    if (bookingFor === 'child') {
      form.setValue('firstName', '');
      form.setValue('lastName', '');
      form.setValue('dateOfBirth', '');
      form.setValue('gender', undefined);
      form.setValue('skillLevel', undefined);
      form.setValue('medicalNotes', '');
      form.setValue('useCustomerAsEmergencyContact', true);
    } else if (bookingFor === 'myself') {
      form.setValue('useCustomerAsEmergencyContact', false);
    }
  }, [bookingFor, form]);

  // Focus and scroll date of birth field when selecting "Myself" (skip when profile already complete)
  React.useEffect(() => {
    if (bookingFor !== 'myself' || canSkipMyself) return () => {};
    const timer = setTimeout(() => {
      const input = document.getElementById('dateOfBirth');
      input?.focus();
      input?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
    return () => clearTimeout(timer);
  }, [bookingFor, canSkipMyself]);

  // When firstName/lastName change in myself mode, sync to customer fields and emergency contact
  const handleMyselfFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    form.setValue('firstName', value);
    form.setValue('customerFirstName', value);
  };
  const handleMyselfLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    form.setValue('lastName', value);
    form.setValue('customerLastName', value);
  };

  const handleExistingChildClick = (student: Student) => {
    setConfirmStudent(student);
  };

  const handleConfirmExistingChild = () => {
    if (confirmStudent && onSelectExistingStudent) {
      onSelectExistingStudent(confirmStudent);
      setConfirmStudent(null);
    }
  };

  const handleCancelConfirm = () => {
    setConfirmStudent(null);
    setConfirmMyself(false);
  };

  const handleConfirmMyself = () => {
    if (onSelectMyself) {
      onSelectMyself();
      setConfirmMyself(false);
    }
  };

  if (canSkipMyself && lockBookingFor === 'myself') {
    return (
      <>
        <div className="space-y-6">
          {onBackFromWaitlistForm && (
            <div>
              <button
                type="button"
                onClick={onBackFromWaitlistForm}
                className="text-sm text-blue-600 hover:underline dark:text-blue-400"
              >
                ← Back to student selection
              </button>
            </div>
          )}
          <div>
            <h3 className="text-lg font-medium text-foreground mb-2">Join the waitlist for yourself</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Your student profile is already on file for this academy. Confirm to join the waitlist.
            </p>
            <Button type="button" onClick={() => setConfirmMyself(true)} isLoading={isSubmitting}>
              Join waitlist
            </Button>
          </div>
        </div>
        <Dialog open={confirmMyself} onOpenChange={(open) => !open && handleCancelConfirm()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm waitlist</DialogTitle>
              <DialogDescription>
                Join the waitlist for <strong>yourself</strong>? You&apos;ll be notified when a spot opens.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={handleCancelConfirm}>
                Cancel
              </Button>
              <Button onClick={handleConfirmMyself} isLoading={isSubmitting}>
                Yes, join waitlist
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {onBackFromWaitlistForm && (
          <div className="mb-2">
            <button
              type="button"
              onClick={onBackFromWaitlistForm}
              className="text-sm text-blue-600 hover:underline dark:text-blue-400"
            >
              ← Back to student selection
            </button>
          </div>
        )}
        {!lockBookingFor && (
        <div className="mb-6">
          <h3 className="text-lg font-medium text-foreground mb-4">Who are you booking for?</h3>
          <div className="flex flex-wrap gap-3">
            <label
              className={cn(
                'flex items-center p-3 rounded-lg cursor-pointer flex-1 min-w-[120px] transition-colors border border-border',
                bookingFor === 'child'
                  ? 'bg-blue-50 dark:bg-blue-950/30 ring-2 ring-blue-500 ring-offset-2 ring-offset-background dark:ring-offset-background'
                  : 'hover:bg-muted/50'
              )}
            >
              <input
                type="radio"
                value="child"
                {...form.register('bookingFor')}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-input"
              />
              <span className={cn('ml-2', bookingFor === 'child' ? 'text-blue-900 dark:text-blue-100 font-medium' : 'text-muted-foreground')}>My Child</span>
            </label>
            {canSkipMyself ? (
              <button
                type="button"
                onClick={() => setConfirmMyself(true)}
                className="flex min-w-[120px] flex-1 items-center rounded-lg border border-blue-200 bg-blue-50/50 p-3 text-left hover:bg-muted/50 dark:border-blue-800 dark:bg-blue-950/30"
              >
                <User className="mr-2 h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" />
                <span className="text-foreground">
                  Myself
                  {(authenticatedUser?.firstName || authStoreUser?.firstName) && (
                    <span className="ml-1 text-muted-foreground">
                      ({authenticatedUser?.firstName || authStoreUser?.firstName}{' '}
                      {authenticatedUser?.lastName || authStoreUser?.lastName})
                    </span>
                  )}
                </span>
              </button>
            ) : (
              <label
                className={cn(
                  'flex items-center p-3 rounded-lg cursor-pointer flex-1 min-w-[120px] transition-colors border border-border',
                  bookingFor === 'myself'
                    ? 'bg-blue-50 dark:bg-blue-950/30 ring-2 ring-blue-500 ring-offset-2 ring-offset-background dark:ring-offset-background'
                    : 'hover:bg-muted/50'
                )}
              >
                <input
                  type="radio"
                  value="myself"
                  {...form.register('bookingFor')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-input"
                />
                <span className={cn('ml-2', bookingFor === 'myself' ? 'text-blue-900 dark:text-blue-100 font-medium' : 'text-muted-foreground')}>Myself</span>
              </label>
            )}
            {hasExistingChildren &&
              existingStudents.map((student) => (
                <button
                  key={student.id}
                  type="button"
                  onClick={() => handleExistingChildClick(student)}
                  className="flex min-w-[120px] flex-1 items-center rounded-lg border border-blue-200 bg-blue-50/50 p-3 text-left hover:bg-muted/50 dark:border-blue-800 dark:bg-blue-950/30"
                >
                  <User className="mr-2 h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" />
                  <span className="text-foreground">
                    {student.firstName} {student.lastName}
                  </span>
                </button>
              ))}
          </div>
        </div>
        )}

      <h3 className="text-lg font-medium text-foreground mb-6">
        {variant === 'waitlist'
          ? lockBookingFor === 'myself'
            ? 'Your details'
            : 'New student details'
          : bookingFor === 'myself'
            ? 'Your Information'
            : 'Student Information'}
      </h3>
      {authenticatedUser && authStoreUser && (
        <div className="mb-4 rounded-md border border-green-200 bg-green-50 p-4 dark:border-green-800/80 dark:bg-green-950/45">
          <div className="flex items-center">
            <CheckCircle className="mr-2 h-5 w-5 shrink-0 text-green-600 dark:text-green-400" />
            <div>
              <p className="text-sm font-medium text-green-900 dark:text-green-100">
                Signed in as {authenticatedUser.firstName} {authenticatedUser.lastName}
              </p>
              <p className="text-xs text-green-800 dark:text-green-300/95">{authenticatedUser.email}</p>
            </div>
          </div>
        </div>
      )}
      {(bookingFor === 'child' || bookingFor === 'myself') && showStudentFields && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field data-invalid={!!form.formState.errors.firstName}>
              <FieldLabel htmlFor="firstName">
                {bookingFor === 'myself' ? 'Your First Name *' : 'Student First Name *'}
              </FieldLabel>
              {bookingFor === 'myself' ? (
                <Input
                  id="firstName"
                  aria-invalid={!!form.formState.errors.firstName}
                  value={form.watch('firstName')}
                  onChange={handleMyselfFirstNameChange}
                  placeholder="Enter your first name"
                />
              ) : (
                <Input id="firstName" aria-invalid={!!form.formState.errors.firstName} {...form.register('firstName')} placeholder="Enter student first name" />
              )}
              <FieldError>{form.formState.errors.firstName?.message}</FieldError>
            </Field>
            <Field data-invalid={!!form.formState.errors.lastName}>
              <FieldLabel htmlFor="lastName">
                {bookingFor === 'myself' ? 'Your Last Name *' : 'Student Last Name *'}
              </FieldLabel>
              {bookingFor === 'myself' ? (
                <Input
                  id="lastName"
                  aria-invalid={!!form.formState.errors.lastName}
                  value={form.watch('lastName')}
                  onChange={handleMyselfLastNameChange}
                  placeholder="Enter your last name"
                />
              ) : (
                <Input id="lastName" aria-invalid={!!form.formState.errors.lastName} {...form.register('lastName')} placeholder="Enter student last name" />
              )}
              <FieldError>{form.formState.errors.lastName?.message}</FieldError>
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <Field data-invalid={!!form.formState.errors.dateOfBirth}>
              <FieldLabel htmlFor="dateOfBirth">
                {bookingFor === 'myself' ? 'Your Date of Birth *' : 'Date of Birth *'}
              </FieldLabel>
              <Input
                id="dateOfBirth"
                type="date"
                aria-invalid={!!form.formState.errors.dateOfBirth}
                className={bookingFor === 'myself' ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-background' : undefined}
                {...form.register('dateOfBirth')}
              />
              <FieldError>{form.formState.errors.dateOfBirth?.message}</FieldError>
            </Field>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Gender *</label>
              <select
                {...form.register('gender')}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-ring [color-scheme:light] dark:[color-scheme:dark]"
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
              {form.formState.errors.gender && (
                <p className="text-sm text-destructive mt-1">{form.formState.errors.gender.message}</p>
              )}
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-foreground mb-1">Skill level</label>
            <select
              {...form.register('skillLevel')}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-ring [color-scheme:light] dark:[color-scheme:dark]"
            >
              <option value="">Select skill level (optional)</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-foreground mb-1">Medical Notes</label>
            <textarea
              {...form.register('medicalNotes')}
              placeholder="Any medical conditions or notes..."
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-ring [color-scheme:light] dark:[color-scheme:dark]"
              rows={3}
            />
          </div>
        </>
      )}
      <div className="border-t border-border pt-4">
        <h4 className="text-base font-medium text-foreground mb-4">
          {useMergedGuardianContact ? 'Contact and parent/guardian' : 'Your Contact Information'}
        </h4>
        <p className="text-sm text-muted-foreground mb-4">
          {variant === 'waitlist'
            ? "We'll use this to contact you when a spot opens."
            : "We'll use this to send booking and payment receipts."}
        </p>
        {bookingFor === 'child' ? (
          <label className="mb-4 flex items-start gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4"
              {...form.register('useCustomerAsEmergencyContact')}
              onChange={(e) => {
                form.setValue('useCustomerAsEmergencyContact', e.target.checked);
              }}
            />
            <span>The booking contact is the parent/guardian</span>
          </label>
        ) : null}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field data-invalid={!!form.formState.errors.customerFirstName}>
            <FieldLabel htmlFor="customerFirstName">Your First Name *</FieldLabel>
            <Input
              id="customerFirstName"
              aria-invalid={!!form.formState.errors.customerFirstName}
              {...form.register('customerFirstName')}
              placeholder="Enter your first name"
            />
            <FieldError>{form.formState.errors.customerFirstName?.message}</FieldError>
          </Field>
          <Field data-invalid={!!form.formState.errors.customerLastName}>
            <FieldLabel htmlFor="customerLastName">Your Last Name *</FieldLabel>
            <Input
              id="customerLastName"
              aria-invalid={!!form.formState.errors.customerLastName}
              {...form.register('customerLastName')}
              placeholder="Enter your last name"
            />
            <FieldError>{form.formState.errors.customerLastName?.message}</FieldError>
          </Field>
        </div>
        <Field data-invalid={!!form.formState.errors.customerEmail} className="mt-4">
          <FieldLabel htmlFor="customerEmail">Your Email *</FieldLabel>
          <Input id="customerEmail" type="email" aria-invalid={!!form.formState.errors.customerEmail} {...form.register('customerEmail')} placeholder="Enter your email" />
          <FieldError>{form.formState.errors.customerEmail?.message}</FieldError>
        </Field>
        <div className="mt-4">
          <PhoneField
            control={form.control}
            name="customerPhone"
            label={smsOptIn ? 'Mobile number *' : 'Phone (optional)'}
            id="customerPhone"
            purpose={smsOptIn ? 'sms_notifications' : 'emergency_contact'}
            defaultCountrySource={defaultCountrySource}
            placeholder="Phone number"
          />
          <FieldError>{form.formState.errors.customerPhone?.message}</FieldError>
        </div>
        {useMergedGuardianContact ? (
          <Field data-invalid={!!form.formState.errors.emergencyContact?.relationship} className="mt-4">
            <FieldLabel htmlFor="emergencyContact-relationship">Relationship *</FieldLabel>
            <Input
              id="emergencyContact-relationship"
              aria-invalid={!!form.formState.errors.emergencyContact?.relationship}
              {...form.register('emergencyContact.relationship')}
              placeholder="e.g., Parent, Guardian"
            />
            <FieldError>{form.formState.errors.emergencyContact?.relationship?.message}</FieldError>
          </Field>
        ) : null}
        <div className="mt-4 space-y-2">
          <label className="flex items-start gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4"
              {...form.register('smsOptIn')}
            />
            <span>
              Send me SMS alerts for urgent lesson changes and waitlist spots (transactional only; opt in per
              academy)
            </span>
          </label>
        </div>
      </div>
      {!useMergedGuardianContact ? (
      <div className="border-t border-border pt-4">
        <h4 className="text-base font-medium text-foreground mb-4">Emergency Contact</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field data-invalid={!!form.formState.errors.emergencyContact?.name}>
            <FieldLabel htmlFor="emergencyContact-name">Contact Name</FieldLabel>
            <Input id="emergencyContact-name" aria-invalid={!!form.formState.errors.emergencyContact?.name} {...form.register('emergencyContact.name')} placeholder="Enter contact name" />
            <FieldError>{form.formState.errors.emergencyContact?.name?.message}</FieldError>
          </Field>
          <PhoneField
            control={form.control}
            name="emergencyContact.phone"
            label="Phone Number"
            id="emergencyContact-phone"
            purpose="emergency_contact"
            defaultCountrySource={defaultCountrySource}
            placeholder="Phone number"
          />
        </div>
        <Field data-invalid={!!form.formState.errors.emergencyContact?.relationship} className="mt-4">
          <FieldLabel htmlFor="emergencyContact-relationship-separate">Relationship</FieldLabel>
          <Input id="emergencyContact-relationship-separate" aria-invalid={!!form.formState.errors.emergencyContact?.relationship} {...form.register('emergencyContact.relationship')} placeholder="e.g., Parent, Guardian, Sibling" />
          <FieldError>{form.formState.errors.emergencyContact?.relationship?.message}</FieldError>
        </Field>
      </div>
      ) : null}
      <Button type="submit" className="w-full" isLoading={isSubmitting}>
        {variant === 'waitlist'
          ? 'Join waitlist'
          : hasPaymentProcessing
            ? 'Continue to Payment'
            : 'Confirm Booking'}
      </Button>
    </form>

      <Dialog open={!!confirmStudent || confirmMyself} onOpenChange={(open) => !open && handleCancelConfirm()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{variant === 'waitlist' ? 'Confirm waitlist' : 'Confirm booking'}</DialogTitle>
            <DialogDescription>
              {confirmStudent ? (
                <>
                  Book this lesson for <strong>{confirmStudent.firstName} {confirmStudent.lastName}</strong>? You&apos;ll proceed directly to payment.
                </>
              ) : confirmMyself ? (
                variant === 'waitlist' ? (
                  <>
                    Join the waitlist for <strong>yourself</strong>? You&apos;ll be notified when a spot opens.
                  </>
                ) : (
                  <>
                    Book this lesson for <strong>yourself</strong>? You&apos;ll proceed directly to payment.
                  </>
                )
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelConfirm}>
              Cancel
            </Button>
            <Button
              onClick={confirmMyself ? handleConfirmMyself : handleConfirmExistingChild}
              isLoading={isSubmitting}
            >
              Yes, continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
